// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import range from 'lodash/range';
import {jsx} from 'panel';
import {VNode} from 'snabbdom';

import {SvgIconEl} from 'common/components/svg-icon';
import {handleKeyPress} from 'common/util/focusable';
import {LocalizationStore} from 'common/i18n/store';

import {SidenavItemEl} from './sidenav-item';
import {NestingOrder, SidenavSection as SidenavSectionType, SidenavSectionItem} from './types';
import type {Sidenav} from './index';

function Loader() {
  return (
    <div sel='.loader' attrs={{elref: `sidenavLoader`}}>
      <div sel='.mp-skeleton-header' />
      {range(9).map((_) => (
        <div sel='.mp-skeleton-large' />
      ))}
    </div>
  );
}

function SidenavItemWithNestedItems(props: {
  section: SidenavSectionType;
  item: SidenavSectionItem;
  idx: number;
  sectionIdx: number;
  selectedItemValue: string;
  hoveredItemValues: Set<string>;
  sectionDragDisabled: boolean;
  activeDragSectionIdx: number;
  onItemDrop: (ev: CustomEvent) => void;
  onItemDragStart: (ev: CustomEvent) => void;
  onItemDragEnd: (ev: CustomEvent) => void;
  onSelectItem: (ev: CustomEvent) => void;
  toggleItemChildren: (ev: CustomEvent) => void;
  onItemOverflowMenuChange: (ev: CustomEvent) => void;
  onItemOverflowMenuSelect: (ev: CustomEvent) => void;
  onItemMouseEntered: (item: SidenavSectionItem) => void;
  preventDefaultNavigate: (ev: CustomEvent) => void;
  isNestedDashboardsEnabled?: boolean;
  expandedItemSet?: Set<string>;
  deepLevel?: number;
  parentItemId?: string;
  nestedOrder?: NestingOrder;
}) {
  const section = props.section;
  const item = props.item;
  const idx = props.idx;
  const sectionIdx = props.sectionIdx;
  const selectedItemValue = props.selectedItemValue;
  const hoveredItemValues = props.hoveredItemValues;
  const sectionDragDisabled = props.sectionDragDisabled;
  const activeDragSectionIdx = props.activeDragSectionIdx;
  const onItemDrop = props.onItemDrop;
  const onItemDragStart = props.onItemDragStart;
  const onItemDragEnd = props.onItemDragEnd;
  const onSelectItem = props.onSelectItem;
  const toggleItemChildren = props.toggleItemChildren;
  const onItemOverflowMenuChange = props.onItemOverflowMenuChange;
  const onItemOverflowMenuSelect = props.onItemOverflowMenuSelect;
  const onItemMouseEntered = props.onItemMouseEntered;
  const preventDefaultNavigate = props.preventDefaultNavigate;
  const isNestedDashboardsEnabled = props.isNestedDashboardsEnabled;
  const expandedItemSet = props.expandedItemSet;
  const deepLevel = props.deepLevel || 0;
  const parentItemId = props.parentItemId || ``;
  const nestedOrder = props.nestedOrder || NestingOrder.First;

  // This allows us to create an id for dashboard items that will remain the same when items are re-ordered
  const itemId = parentItemId ? `${parentItemId}-${item.value}` : `${sectionIdx}-${item.value}`;
  const nestedItemsToShow = expandedItemSet.has(itemId) && item.nestedItems?.length ? item.nestedItems : [];

  return (
    <div sel='.sidenav-item'>
      <SidenavItemEl
        attrs={{
          ...(selectedItemValue && {'is-selected': item.value === selectedItemValue}),
          'is-new': item.isNew && !hoveredItemValues?.has(item.value),
          'is-draggable': section.dragDropEnabled,
          'is-drag-disabled': sectionDragDisabled,
          'can-drop-item': !item.dropDisabled && Number.isInteger(activeDragSectionIdx),
          'can-nest-item': !item.nestDisabled,
          'label-tab-size': deepLevel,
          'is-nested-dashboards-enabled': isNestedDashboardsEnabled,
          'can-expand': item.nestedItems?.length > 0,
          'is-expanded': expandedItemSet.has(itemId),
          'nested-order': nestedOrder,
        }}
        on={{
          itemDrop: onItemDrop,
          itemDragStart: onItemDragStart,
          itemDragEnd: onItemDragEnd,
          itemClick: onSelectItem,
          itemExpand: toggleItemChildren,
          overflowMenuChange: onItemOverflowMenuChange,
          overflowMenuSelect: onItemOverflowMenuSelect,
          mouseenter: () => onItemMouseEntered(item),
        }}
        props={{preventDefaultNavigate, item: {...item, itemIdx: idx, sectionIdx, itemId}}}
      />
      <div sel='.sidenav-nested-items' class={{'is-selected': selectedItemValue && selectedItemValue === item.value}}>
        {nestedItemsToShow.map((subItem, index) => (
          <SidenavItemWithNestedItems
            section={section}
            item={subItem}
            sectionIdx={sectionIdx}
            idx={idx}
            selectedItemValue={selectedItemValue}
            hoveredItemValues={hoveredItemValues}
            sectionDragDisabled={sectionDragDisabled}
            activeDragSectionIdx={activeDragSectionIdx}
            onItemDrop={onItemDrop}
            onItemDragStart={onItemDragStart}
            onItemDragEnd={onItemDragEnd}
            onSelectItem={onSelectItem}
            toggleItemChildren={toggleItemChildren}
            onItemOverflowMenuChange={onItemOverflowMenuChange}
            onItemOverflowMenuSelect={onItemOverflowMenuSelect}
            onItemMouseEntered={onItemMouseEntered}
            preventDefaultNavigate={preventDefaultNavigate}
            deepLevel={deepLevel + 1}
            isNestedDashboardsEnabled={isNestedDashboardsEnabled}
            expandedItemSet={expandedItemSet}
            parentItemId={itemId}
            nestedOrder={
              index === item.nestedItems.length - 1 &&
              (nestedOrder === NestingOrder.Last || nestedOrder === NestingOrder.First)
                ? NestingOrder.Last
                : NestingOrder.Middle
            }
          />
        ))}
      </div>
    </div>
  );
}

function SidenavSection(
  props: {
    section: SidenavSectionType;
    sectionDragDisabled: boolean;
    isExpanded: boolean;
    onToggleSection: () => void;
    i18n: LocalizationStore;
  },
  sectionItemsChildren: VNode[],
) {
  const section = props.section;
  const sectionDragDisabled = props.sectionDragDisabled;
  const isExpanded = props.isExpanded;
  const onToggleSection = props.onToggleSection;
  const {print} = props.i18n;

  const isSectionCollapsible = section.label && section.isCollapsible;
  return (
    <div sel='.section' attrs={{elref: `sidenavSection`}} class={{'active-dragging-disabled': sectionDragDisabled}}>
      {section.label && (
        <div
          sel='.section-header'
          attrs={{elref: `sidenavSectionHeader`, tabindex: 0}}
          on={isSectionCollapsible && {click: onToggleSection, keypress: handleKeyPress}}
          class={{'is-collapsible': isSectionCollapsible}}
        >
          <SvgIconEl attrs={{icon: isExpanded ? `caret-down` : `caret-right`}} />
          <div sel='.section-header-label'>{print(section.label)}</div>
        </div>
      )}

      {isExpanded && (
        <div sel='.section-items-list' attrs={{role: `list`}}>
          {sectionItemsChildren}
        </div>
      )}
    </div>
  );
}

export function template(this: Sidenav) {
  const sections = this.state.sections;
  const {print} = this.getContext(`i18n`);

  const selectedItemValue = this.helpers.getSelectedValue();

  const sidenavContent = this.attr(`loading`) ? (
    <Loader />
  ) : (
    sections.map((section, sectionIdx) => {
      const sectionDragDisabled =
        Number.isInteger(this.state.activeDragSectionIdx) && this.state.activeDragSectionIdx !== sectionIdx;

      const isExpanded = !this.state.collapsedSectionSet.has(section.key || print(section.label));

      // we do this instead of doing `sections.filter()` because if we filter first, we change the sectionIdx value
      // and we use it as a reference in some events to know what section was updated
      if (section.items.length === 0) {
        return null;
      }

      return (
        <SidenavSection
          section={section}
          sectionDragDisabled={sectionDragDisabled}
          isExpanded={isExpanded}
          onToggleSection={() => this.helpers.toggleSection(section)}
          i18n={this.getContext(`i18n`)}
        >
          {isExpanded &&
            section.items
              .filter(
                (item) =>
                  !item.featureFlag || this.getContext(`currentProject`).get().value?.hasFeatureFlag(item.featureFlag),
              )
              .map((item, idx) => (
                <div
                  sel='.sidenav-item .sidenav-nested-items'
                  class={{
                    ...(selectedItemValue && {'is-selected': selectedItemValue === item.value}),
                    'is-child-selected': selectedItemValue && this.isItemOrChildSelected(item, selectedItemValue),
                  }}
                  attrs={{
                    role: `listitem`,
                    'aria-label': print(item.label),
                    value: item.value,
                  }}
                  hook={{
                    insert: this.helpers.handleSidenavItemInserted,
                  }}
                >
                  <SidenavItemWithNestedItems
                    section={section}
                    item={item}
                    idx={idx}
                    sectionIdx={sectionIdx}
                    selectedItemValue={selectedItemValue}
                    hoveredItemValues={this.state.hoveredItemValues}
                    sectionDragDisabled={sectionDragDisabled}
                    activeDragSectionIdx={this.state.activeDragSectionIdx}
                    onItemDrop={this.helpers.onItemDrop}
                    onItemDragStart={this.helpers.onItemDragStart}
                    onItemDragEnd={this.helpers.onItemDragEnd}
                    onSelectItem={this.helpers.onSelectItem}
                    toggleItemChildren={this.helpers.toggleItemChildren}
                    onItemOverflowMenuChange={this.helpers.onItemOverflowMenuChange}
                    onItemOverflowMenuSelect={this.helpers.onItemOverflowMenuSelect}
                    onItemMouseEntered={this.helpers.onItemMouseEntered}
                    preventDefaultNavigate={this.preventDefaultNavigate}
                    isNestedDashboardsEnabled={
                      this.attr(`is-nested-dashboards-enabled`) && !this.state.draggingItem?.nestDisabled
                    }
                    expandedItemSet={this.state.expandedItemSet}
                  />
                </div>
              ))}
        </SidenavSection>
      );
    })
  );

  return (
    <div sel='.sidebar'>
      <div sel='.wrapper'>
        <div
          sel='.sections-container'
          hook={{insert: this.helpers.onSectionsInserted}}
          attrs={{elref: `sectionsContainer`}}
        >
          <div sel='.sections'>{sidenavContent}</div>
        </div>
      </div>
    </div>
  );
}
